"use client";

import { motion } from "framer-motion";
import { useTranslations } from "next-intl";

import AccountsSummary from "@/components/accounts-summary";
import DashboardTabsHeader from "@/components/dashboard/dashboard-tabs-header";
import ResumeOnboardingBanner from "@/components/dashboard/resume-onboarding-banner";
import { ClientErrorBoundary } from "@/components/error/ClientErrorBoundary";
import LedgerEntryList from "@/components/ledger/ledger-entry-list";
import ProcessingPaymentsList from "@/components/payments/processing-payments-list";
import UnconfirmedPaymentsList from "@/components/payments/unconfirmed-payments-list";
import TotalBalance from "@/components/total-balance";
import { useAccountSummary } from "@/hooks/useAccountSummary";
import { useDashboardMountRefresh } from "@/hooks/useDashboardMountRefresh";
import { useDashboardTabs } from "@/hooks/useDashboardTabs";
import { DashboardHeaderProvider, LedgerEntryListProvider } from "@/providers";

export default function DashboardPage() {
  const tCommon = useTranslations("common");
  const {
    summaryItems,
    showViewAllAccountsCard,
    baseCurrency,
    totalBalance,
    isLoading,
    isStillCalculating,
    refetch,
    isUninitialized,
  } = useAccountSummary();

  const { activeTab } = useDashboardTabs();

  useDashboardMountRefresh({
    refetchBalances: refetch,
    isBalancesUninitialized: isUninitialized,
    activeTab,
  });

  return (
    <DashboardHeaderProvider>
      <div className="flex flex-col gap-3">
        <ResumeOnboardingBanner />
        <motion.div
          key="total-balance"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <TotalBalance
            balance={totalBalance}
            currency={baseCurrency}
            isBaseCurrency
            totalBalanceText={tCommon("totalAvailableBalance")}
            showDepositAccountSelect
            isStillCalculating={isStillCalculating}
            forceEnablePay
          />
        </motion.div>

        <motion.div
          key="accounts-summary"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <ClientErrorBoundary>
            <AccountsSummary
              accounts={summaryItems}
              isLoading={isLoading}
              baseCurrency={baseCurrency}
              showViewAllAccountsCard={showViewAllAccountsCard}
            />
          </ClientErrorBoundary>
        </motion.div>

        <DashboardTabsHeader />

        <div id="dashboard-tab-content" className="-mt-3" />
        {activeTab === "activity" ? (
          <div key="activity" className="animate-list-mount">
            <LedgerEntryListProvider>
              <LedgerEntryList />
            </LedgerEntryListProvider>
          </div>
        ) : activeTab === "processing" ? (
          <div key="processing" className="animate-list-mount">
            <ProcessingPaymentsList />
          </div>
        ) : (
          <div key="pending" className="animate-list-mount">
            <UnconfirmedPaymentsList />
          </div>
        )}
      </div>
    </DashboardHeaderProvider>
  );
}
